// Domain model for the Hedging Grid CoPilot (CR-20260619-001, M2-2).
//
// Frontend-owned (ADR-026): NOTHING here persists server-side. These are idiomatic
// TypeScript discriminated unions / entities — the source of truth the questionnaires
// (M2-3/4/5) populate and the validators (copilot-validation) gate on. The wire mapping
// to the backend protobuf DTOs (the JSON.stringify shell-router command path) lives in
// copilot-operations.ts + copilot-mapper.ts; do NOT serialize these domain types directly.

import { parseOptionTicker } from '../../options-common/options.model';

export type TabKind = 'select' | 'adjust' | 'close';

// ---- Questionnaire inputs (FR-3..28) --------------------------------------------------

// Price-range form — EXACTLY ONE variant per Run (FR-6, FR-15, C-6). Discriminated union.
//
// 'fillGap' ADDED by CR-20260724-001 (Adjust tab only). It takes the SAME two strikes as 'bounded'
// and differs only in what the engine is PERMITTED to do outside them: Fill-Gap must not worsen
// anything beyond the two strikes, where 'bounded' (the engine's Range form) may sell the beyond to
// finance the deal. Two forms, one strike pair — so they share the control, never the meaning.
// 'useExpectedRange' ADDED by CR-20260820-002 (Adjust tab, RANGE form only). ON means the two
// edges are NOT the trader's: the backend measures them from the option-implied expected move
// (the walked at-the-money anchor, reached out by the ATM straddle on each side) and the strikes
// travel absent. Mutually exclusive with typing them, which the panel enforces by disabling and
// clearing both dropdowns.
//
// ⚠ A FLAG ON 'bounded', NOT A FIFTH KIND, and the reason is not stylistic: `showMaxRisk` in
// hg-copilot-risk-inputs is `priceKind === 'bounded'`, so a fifth kind would take the entire Max
// Risk section - levels, risk side, half risk - off the screen. It is also the truth: this IS the
// Range form, answering the same question about the same extent; only who chose the edges differs.
//
// ⚠ NOT ON 'fillGap'. Range only for now (owner, 2026-08-20: "we now don't think about fill gap
// and the other modes"). Fill Gap shares the strike-pair CONTROL, not this flag - see
// isPriceRangeValid, which keeps demanding both strikes there.
export type PriceRange =
    | { kind: 'bounded'; startStrike: number; endStrike: number; useExpectedRange?: boolean }  // start < end (FR-7, FR-26)
    | { kind: 'fillGap'; startStrike: number; endStrike: number }          // start < end, same rule as bounded
    // THE TWO-SIDED FOLLOW-THROUGH (CR-20260824-015 — the form REWRITTEN, not amended; the
    // direction-era { startStrike, direction } variant is retired). Each side is null when
    // disabled; an enabled side carries its own strike, its own minimum (typed in the mode its
    // flag names, converted to dollars at the wire like every money figure) and its own Harvest.
    // closeRemaining is ONE toggle for both sides: remaining = every node no enabled side claims;
    // false = leave it exactly as the book has it (the default), true = sell it out to flat zero.
    | {
        kind: 'followThrough';
        up: FollowThroughSideInput | null;
        down: FollowThroughSideInput | null;
        closeRemaining: boolean;
      }
    | { kind: 'wall' };                                                     // entire strike ladder (FR-9)

// One side of the two-sided Follow-Through (CR-20260824-015).
export interface FollowThroughSideInput {
    startStrike: number;               // NaN when the dropdown is empty — the validity gate reads it
    minProfit: number;                 // as typed; NaN when empty
    minProfitNormalized: boolean;      // discriminator: the amount is a normalized value, not plain dollars
    harvest: boolean;
}

// The canonical relative risk levels (FR-12). Renamed 2026-07-09 (owner) from
// Conservative/Moderate/Aggressive -> Low/Moderate/Aggressive; the legacy Conservative/High labels
// AND the older Low/Medium/High labels alias-map to these (FR-13, copilot-validation.normalizeRelativeLevel);
// they are NEVER a distinct extra option.
//
// 'All' ADDED 2026-08-17 (owner: "low, moderate, aggressive, and then all"). It is NOT a fourth risk
// appetite — it asks for every level in ONE run so the answers can be compared side by side, and the
// backend fans out over the levels rather than reading it as a depth. Adjust tab only: the Select tab
// is served by a different engine that does not know the value, so the questionnaire opts in.
export type RelativeRiskLevel = 'Low' | 'Moderate' | 'Aggressive' | 'All';

// THE DOLLAR ARM'S PLACEMENT LEVELS (CR-20260819-002 R-2). The Dollar arm is the same risk machine
// as the Relative arm fed a different number (R-1) - the level dictates WHERE the risk is placed on
// both arms - so the Dollar tab carries the same selector WITHOUT Low: "low assumes no risk, so it
// has to be moderate or aggressive placement". A $0 amount is the dollar spelling of no-risk. 'All'
// fans out Moderate + Aggressive at the typed depth (R-10). Excluding 'Low' in the TYPE is the
// guard: a dollar ceiling asking for Low is unrepresentable, so nothing has to validate it away.
export type DollarRiskLevel = 'Moderate' | 'Aggressive' | 'All';

// WHICH SIDE OF THE RANGE THE RUN MAY EXTEND RISK TO (owner, vrequest 2026-08-18). The brake sells
// new risk beyond an edge of the range; this says which edge(s) it is allowed to sell beyond —
// 'up' = the upper edge only, 'down' = the lower edge only, 'both' = both edges.
//
// 'auto' lets the ENGINE pick the side from the current market situation, and it is REAL since
// CR-20260819-001: the backend weighs each edge's risk structure by the total credit it pays and
// resolves the side before composing (tie within 5% of the larger = acts as Both; a side it cannot
// weigh FAILS the run with the reason, never substitutes). The wire still carries 'auto' so the
// trace can say what the trader asked for. Auto applies to BOTH max-risk arms since CR-20260819-002
// (R-9 there superseded the "Auto+Dollar diverges by design" note).
export type RiskSide = 'up' | 'down' | 'both' | 'auto';

// Max-risk CEILING the user inputs (FR-11/12/13). A questionnaire INPUT only — the skill no longer
// reports a max-risk attribute on a Solution (contract revised 2026-06-19), so there is nothing for
// the host to compare it against or enforce.
export type MaxRiskCeiling =
    // normalized = the entered amount is a NORMALIZED value, not plain dollars — a discriminator the
    // skill reads to interpret the number (owner 2026-06-22). Only the dollar variant carries it.
    //
    // level ADDED by CR-20260819-002 (R-2): the dollar arm carries its own placement level -
    // Moderate | Aggressive | All, no Low. The amount replaces only the risk REFERENCE number; the
    // level dictates placement exactly as on the relative arm (R-1). The minimum profit still owns
    // the fill/plateau height - the amount has no say inside the range (R-6).
    | { kind: 'dollar'; amount: number; normalized: boolean; level: DollarRiskLevel }
    | { kind: 'relative'; level: RelativeRiskLevel };

// Date selection — only Select asks a date (FR-5).
export type SelectDate =
    | { kind: 'single'; date: Date }
    | { kind: 'range'; start: Date; end: Date };

export interface SelectInputs {            // FR-3..13
    date: SelectDate;
    priceRange: PriceRange;
    minProfit: number;
    minProfitNormalized: boolean;          // the minProfit number is a normalized value, not plain dollars (owner 2026-06-22)
    maxRisk: MaxRiskCeiling;
}

export interface AdjustInputs {            // FR-14..17
    // The ONE expiration this run addresses (CR-20260814-001).
    //
    // The engine shapes a P&L LINE, and a P&L line is defined per expiration. A portfolio may hold
    // hedges in several expirations, and until this field existed the run could not say which line it
    // was being asked about — the backend refused the whole request by name ("This portfolio holds
    // hedges in more than one expiration"), leaving the trader with no way through. SINGLE-select for
    // that same reason: two expirations are two lines, and the engine shapes one.
    //
    // ⚠ DELIBERATELY NOT ON THE WIRE. It selects which POSITIONS are sent on RunCoPilotSkill; the
    // backend then derives the run's expiration from those positions exactly as it always has. See
    // mapAdjustInputs for why adding a wire field would be redundant AND would cost the backstop.
    expiration: Date | null;
    priceRange: PriceRange;
    // ADDED by CR-20260820-002. The Expected Move MODEL behind an expected-range price range, or
    // null on every other form.
    //
    // ⚠ THE MODEL, NEVER A COMPUTED BAND. The panel is perfectly capable of working the band out —
    // that is the trap, not the solution. Resolving it frontend-side is prohibited (owner ruling
    // CR-20260715-001, governed by ADR-038); the backend measures the edges from its own market
    // read and this field only says which model to measure with.
    //
    // ⚠ Read where the CHECKBOX is gated, so one predicate decides both. Reading it again on the
    // way to the wire would let the control say "available" while the courier says null.
    expectedRangeModel?: { model: string, customPercent: number | null } | null;
    minProfit: number;
    minProfitNormalized: boolean;          // the minProfit number is a normalized value, not plain dollars (owner 2026-06-22)
    maxRisk: MaxRiskCeiling;
    // ADDED by CR-20260724-001, Adjust only. A COMMAND, not a hint: ON means every peak the form
    // permits is capped at the minimum wherever a block can do it; OFF means peaks are untouchable.
    // Giving up profit you already own is the trader's judgement, so the engine never auto-decides.
    harvest: boolean;
    // ADDED by CR-20260818-001 iteration 3, Adjust only, and COMMANDS in the same sense harvest is
    // — the trader's judgement, never auto-detected.
    //
    // riskSide: which edge(s) of the range the run may sell NEW RISK beyond. It qualifies BOTH
    // max-risk arms (CR-20260819-002 R-4/R-9 - the dollar arm is the same machine fed a different
    // number). 'auto' is carried as the trader picked it and RESOLVED BY PRICE at the backend
    // (CR-20260819-001), so the trace says what was chosen and then what won.
    //
    // ⚠ The cut is NOT scoped by this. Taking the beyond down to zero gives payoff back; it does not
    // take on risk. Only the depth BELOW zero is side-scoped.
    riskSide: RiskSide;
    // halfRisk: apply half the 1:1 ceiling — half the requested minimum profit instead of all of it.
    // Relative arm only, and that STAYS under CR-20260819-002 (R-3): on a named dollar amount "if
    // you want half, just go and enter half" - the flag travels false in dollar mode.
    halfRisk: boolean;
    // ⚠ This type deliberately carries NO engine directive and NO strictness flag: the engine has
    // one composer set (2026-08-08) and clean delivery is the only mode. Do not rebuild `engine` /
    // `binaryDelivery` from old conversations - the backend refuses the retired directives by name.
}

// Close target: ALL existing hedges (scoped to the chosen dates, FR-147) or specific hedges.
export type CloseTarget =
    | { kind: 'all' }
    | { kind: 'hedges'; hedgeIds: string[] };

export interface CloseInputs {             // FR-18..22, FR-147, FR-148 (NO priceRange/minProfit/maxRisk)
    dates: Date[];                         // multi-select of EXISTING book dates, no range (FR-19, FR-148)
    target: CloseTarget;
}

export type TabInputs = SelectInputs | AdjustInputs | CloseInputs;

// ---- Run + Solution model (FR-30..69) -------------------------------------------------

// Exhaustive RunOutcome (drives UI + logging, NFR-25). 'pending'/'cancelled' are FE-only —
// the backend never emits them (a cancelled Run pushes nothing; pending is pre-push).
export type RunOutcome =
    | 'pending'           // in-flight (the single-flight one)
    | 'succeeded'         // >=1 Solution (FR-56)
    | 'empty'             // 0 Solutions — valid "no suggestion", NOT a failure (FR-57, NFR-14)
    | 'cancelled'         // terminal; user cancelled (FR-43, NFR-11)
    | 'terminalFailed'    // retries exhausted / unrenderable (FR-48, FR-49)
    | 'preSendBlocked';   // context-assembly failed; 0 attempts spent (FR-32..34)

export interface Run {
    id: string;                 // client-minted; echoed in the ESS push for correlation
    tabKind: TabKind;
    sentInputs: TabInputs;      // snapshot of what was sent
    outcome: RunOutcome;
    attempts: number;
    startedAt: number;          // epoch ms
    endedAt: number | null;
    failureDetail: string | null;   // terminalFailed / preSendBlocked plain message (FR-49)
    // What the run could NOT answer while still succeeding — one line per dropped risk level, in
    // the engine's own words (2026-08-20). Distinct from failureDetail, which means the run failed.
    notes: string[];
}

// Leg action (FR-61; contract revised 2026-06-19). The skill sends a human-readable actionType
// string ("Buy To Open", "Sell To Close", ...); the FE parses it to {side, effect}. The EFFECT drives
// the grid op on Preview: open = add a new leg; close = close the whole matching book leg; increase /
// decrease = resize the matching book leg. 'reverse' (owner, 2026-08-11: "sell to reverse and buy to
// reverse") is the crossing trade in one row — through the held position and out the other side; it
// only ever arrives on a card targeting an existing hedge, which applies by its computed outcome, so
// no grid op maps to it directly.
export type LegSide = 'buy' | 'sell';
// 'unknown' is the fail-safe for an unparseable action string — the apply path REJECTS it (never
// silently mis-maps to a real grid op). The backend already validates the action against the closed
// set, so 'unknown' should not occur via the real skill path; this is defense-in-depth.
export type LegEffect = 'open' | 'close' | 'increase' | 'decrease' | 'reverse' | 'unknown';

export interface Leg {                     // FR-61
    quantity: number;
    ticker: string;            // raw full option ticker — the placement key (matches HedgePositionModel.getTicker())
    tickerDisplayName: string; // human-readable ticker (card display; never a raw ticker on screen)
    action: string;            // verbatim skill action string ("Buy To Open") — rendered on the card
    side: LegSide;             // parsed from action
    effect: LegEffect;         // parsed from action
    // Per-leg underlying ticker (e.g. 'SPX','ESH6') for the card's ES->SPX net-price normFactor. Stamped at
    // build time on BOTH paths: local-Close legs copy it from the held position; skill legs derive it from the
    // raw option ticker (parseOptionTicker). Void => treated as the base (normFactor 1). NOT the defaultQuantity.
    asset?: string | null;
    // Owning hedge's groupId (= Hedge Matrix HedgeData.id), set ONLY on locally-generated CLOSE legs
    // (CR-20260619-001 MVP) so Preview can drive that exact hedge's Matrix modification flow. Absent on
    // skill-authored legs.
    groupId?: string;
    // Structured snapshot of the leg's contract, so the read-only before/transaction/after Transactions
    // view can render decomposed Type/Strike/Expiration columns from the Solution alone — WITHOUT
    // re-reading the grid (a previewed close nets the held leg to zero in the working state). On a
    // CLOSE leg it is the held leg's snapshot (CR-20260619-001 MVP); on a skill-authored leg it is
    // parsed off the raw ticker at mapping time (CR-20260918-001 — absent, every cell rendered blank).
    optionType?: string | null;   // the held leg's option type ("Call" | "Put")
    strike?: number | null;        // the held leg's strike — actualStrike for ES (the real instrument strike)
    expiry?: string | null;        // the held leg's expiry, ISO "yyyy-MM-dd"
    // Owning hedge metadata (close-leg snapshot) — the Transactions view's per-hedge block title + colour.
    hedgeLabel?: string | null;    // the hedge's display name, NO "Close" prefix
    hedgeColor?: string | null;    // the hedge's colour
    // CR-20260924-002 (ES mode): the fair-value gap the backend lens used for this leg's contract,
    // stamped on every ES leg of an ES-mode answer (Select and Adjust). The matrix creates a missing
    // ES silo on this number and refuses a preview into a silo built on another. Absent on SPX legs.
    esGap?: number | null;
}

// Parse the skill's human-readable action string -> {side, effect}. Case-insensitive; returns null on
// an unknown string (the backend already validates against the closed set, so this should not fail).
export function parseLegActionType(actionType: string): { side: LegSide; effect: LegEffect } | null {
    if (!actionType) {
        return null;
    }
    const s = actionType.toLowerCase();
    const side: LegSide | null = s.indexOf('buy') === 0 ? 'buy' : (s.indexOf('sell') === 0 ? 'sell' : null);
    let effect: LegEffect | null = null;
    if (s.indexOf('open') !== -1) {
        effect = 'open';
    } else if (s.indexOf('close') !== -1) {
        effect = 'close';
    } else if (s.indexOf('increase') !== -1) {
        effect = 'increase';
    } else if (s.indexOf('decrease') !== -1) {
        effect = 'decrease';
    } else if (s.indexOf('reverse') !== -1) {
        effect = 'reverse';
    }
    if (side === null || effect === null) {
        return null;
    }
    return { side, effect };
}

// ---- Leg display ordering (universal ETS visual rule) ---------------------------------

// The strike to ORDER a leg by. Prefers the structured `strike` snapshot (set on locally-generated
// CLOSE legs — the real instrument strike, actualStrike for ES); for skill-authored legs (Select /
// Adjust) that carry no snapshot, it is parsed from the raw option ticker. Unknown/unparseable =>
// NEGATIVE_INFINITY so the leg sorts LAST in a descending order.
export function legStrikeForSort(leg: Leg): number {
    if (leg && typeof leg.strike === 'number' && !isNaN(leg.strike)) {
        return leg.strike;
    }
    const parsed = leg ? parseOptionTicker(leg.ticker) : undefined;
    if (parsed && typeof parsed.strike === 'number' && !isNaN(parsed.strike)) {
        return parsed.strike;
    }
    return Number.NEGATIVE_INFINITY;
}

// Strike-DESCENDING comparator (highest strike first / at top). The universal ETS visual rule — legs
// read strike-DESC top-down for BOTH Calls and Puts, with no side-aware reordering ("Calls read up /
// Puts read down" is a human-reading mnemonic, not an encoding rule). Shared by EVERY CoPilot surface
// that renders legs (Solution card table + codified preview, Transactions popup) so they never drift.
export function compareLegStrikeDesc(a: Leg, b: Leg): number {
    return legStrikeForSort(b) - legStrikeForSort(a);
}

// Immutable skill-authored content (ADR-029 Value) — rendered VERBATIM, read-only; the host never
// edits or re-judges it (FR-65, FR-127). Host identity (Solution.id) is separate (FR-63); skillId /
// skillUniqueId are carried for traceability only.
// One card inside a package (CR-20260724-001). Content only: Preview and Apply act on the PACKAGE,
// because the cards of a package are necessary together — they are not alternatives to each other.
// What a hedge is left HOLDING once a card's legs are traded (CR-20260724-001 stage 7). Quantity is
// SIGNED — unlike a Leg, whose action carries the side.
export interface Holding {
    quantity: number;                      // SIGNED: long > 0, short < 0
    ticker: string;
    tickerDisplayName: string;
}

export interface PackageCard {
    title: string;                         // named from the legs it shows ("SPX Call Vertical" | "... Custom")
    legs: Leg[];                           // this card's share of the package's traded legs
    // ---- stage 7 (Attribution): a card that CHANGES an existing hedge rather than creating one ----
    // Set => Preview puts THIS hedge into modification and writes `targetOutcome` onto it. Absent =>
    // a new hedge, which is what every card was before stage 7 and remains the permanent fallback.
    targetHedgeId?: string;
    // The engine's own after-state for that hedge.
    // ⚠ AN EMPTY ARRAY NEVER ARRIVES. This comment used to say empty was meaningful and distinct
    // from absent ("the change closes the hedge outright") — it is not expressible on the wire,
    // because protobuf-net writes an empty repeated field as absent. The Matrix's "targets a hedge
    // but carries no targetOutcome" refusal therefore fired on every package that closes a hedge.
    targetOutcome?: Holding[];
    // Whether the card's trade leaves the target hedge holding nothing — stated by the engine,
    // because it cannot be inferred from targetOutcome (see above).
    closesTheHedge?: boolean;
}

export interface SolutionContent {
    skillId: string;
    skillUniqueId: string;
    displayName: string;                   // card title; MAY collide (never an identity, FR-63)
    shortDescription: string;              // shown on the card face
    extendedDescription: string;           // shown in the card's expanded detail
    legs: Leg[];                           // >= 1 (FR-58). The package's TRADED order — what Preview places.
    // The same legs, split across the blocks that produced them, for display. Concatenating every
    // card's legs reproduces `legs` exactly, so what the trader reads is what would be placed.
    // EMPTY on the LLM path and on locally-generated Close solutions — those render `legs` flat.
    cards: PackageCard[];
    // ---- CR-20260806-001: how well this answer fits what was asked (see CoPilotSolutionDto).
    // ⚠ undefined MEANS "not measured" — the LLM path, locally-built Close solutions, and a WALL,
    // which legitimately asks for a standing payoff at both ends. The filter must read "not
    // measured" as "do not hide", never as a failing grade.
    // ⚠ A FOLLOW-THROUGH sends outsideMiss and NOT addedAboveRequest (CR-20260807-001): it stands
    // at one end and promises to break at the other, so exactly one of the two is a question.
    addedAboveRequestDollars?: number;
    outsideMissDollars?: number;
}

// Host-assigned identity — a slot index within the Run, NEVER the Name (FR-63/64). See TC-U-F12.
export type SolutionId = string;

// Solution state (FR-66/86/87/88/90). There are exactly THREE card actions — Preview / Apply /
// Transactions (no user "Block" action); 'blocked' is the state a sibling acquires when ANOTHER
// Solution in the tab is Applied, and it disables ONLY Apply (Preview/Transactions still allowed).
export interface SolutionState {
    previewed: boolean;   // FR-66 (M3 sets this on Preview/un-Preview)
    applied: boolean;     // FR-86/88 (<=1 per tab)
    blocked: boolean;     // a sibling is Applied (FR-87/90); disables only Apply
}

// Solution = Entity (host identity + mutable state) wrapping the immutable Value content (ADR-029).
export interface Solution {
    id: SolutionId;
    content: SolutionContent;
    state: SolutionState;
}

// ---- Conversation aggregate (FR-2, FR-97..114) ----------------------------------------

export type DivergenceState = 'armed' | 'custom';   // re-arms on conversation/portfolio/reload (FR-99/101/103)

export interface Tab {
    kind: TabKind;
    inputs: TabInputs | null;       // questionnaire (null until filled)
    currentRun: Run | null;         // <=1 CURRENT Run per tab (FR-139); a new Run supersedes
    solutions: Solution[];          // from currentRun when 'succeeded'
}

export interface Conversation {
    id: string;
    portfolioId: string;            // bound to ONE portfolio; cross-portfolio reuse forbidden (FR-130, NFR-32)
    divergence: DivergenceState;
    tabs: { select: Tab; adjust: Tab; close: Tab };
}
